import { defineStore } from "pinia";
import { ApiClient } from "@/api-client";
import { i18n } from "@/locales/index";
import * as ApiResponse from "@/api-client/responses";
import { useApplicationStore, useUserStore } from "./index";
import { watch } from "vue";

const apiClient = ApiClient.getInstance();

export const useSystemStore = defineStore("systemStore", {
  state: () => ({
    selectedSystem: null as ApiResponse.Systems.SystemId | null,
    systems: [] as ApiResponse.Systems.SystemId[],
    loadingState: "unset" as loadingStateType,
    failedReason: "",
  }),
  actions: {
    setSelectedSystem(system: ApiResponse.Systems.SystemId | undefined | null) {
      this.selectedSystem = system ?? null;
    },

    async getSystems() {
      // set state
      this.loadingState = "loading";
      this.systems = [];
      try {
        //fetch systems
        const getSystemsResponse = await apiClient.getSystemList();
        this.systems = getSystemsResponse.ids;
      } catch (error) {
        // set state and throw error
        this.loadingState = "failed";
        this.failedReason = i18n.global.t("app.system.loadingData.error", {
          message: error instanceof Error ? error.message : String(error),
        });
        throw error;
      }

      // check if no system in response
      if (this.systems.length === 0) {
        useApplicationStore().addAlert({
          type: "Warning",
          key: "app.system.loadingData.empty",
        });
      }

      // filter out duplicate systems
      const uniqueSystems = new Map<string, ApiResponse.Systems.SystemId>();
      for (const system of this.systems) {
        if (!uniqueSystems.has(system.supplierId)) {
          uniqueSystems.set(system.supplierId, system);
        }
      }
      this.systems = Array.from(uniqueSystems.values());

      // select if only one system
      if (this.systems.length === 1) this.selectedSystem = this.systems[0];

      // set data and state
      this.loadingState = "success";
    },

    initWatcher() {
      // get systems after user was authenticated
      watch(
        () => useUserStore().user,
        async (user) => {
          // has logged out or selected null
          if (!user) {
            this.systems = [];
            this.selectedSystem = null;
            return;
          }
          // get systems
          try {
            await this.getSystems();
          } catch (error) {
            // handle error
            useApplicationStore().addAlert({
              key: "app.system.loadingData.error",
              type: "Alert",
              keyData: {
                message: error instanceof Error ? error.message : String(error),
              },
            });
            return;
          }
        }
      );
    },
  },
});
